import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, Loader2, ShieldCheck, RefreshCw } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function Settings() {
  const navigate = useNavigate();
  const [key, setKey] = useState("");
  const [info, setInfo] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/settings").then((r) => setInfo(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { ensemble_api_key: key });
      toast.success(key ? "API key saved — live refreshes enabled" : "API key cleared — using simulator");
      setKey("");
      await load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen">
      <TopBar />
      <main className="mx-auto max-w-2xl px-4 py-8 sm:px-6 lg:px-8">
        <button data-testid="back-btn" onClick={() => navigate("/")} className="mb-6 flex items-center gap-1.5 text-sm text-neutral-500 transition-colors hover:text-black">
          <ArrowLeft className="h-4 w-4" /> Home
        </button>

        <h1 className="font-display text-3xl font-extrabold tracking-tight">Settings</h1>
        <p className="mt-1 text-neutral-500">Connect a stats provider for live post refreshes.</p>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="pb-card mt-8 p-6">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-neutral-900 text-white"><KeyRound className="h-5 w-5" /></span>
            <div>
              <h2 className="font-display text-lg font-bold">EnsembleData API Key</h2>
              <p className="text-sm text-neutral-500">Used to pull real TikTok & Instagram stats on refresh.</p>
            </div>
          </div>

          <div className="mt-6">
            <Label>API Key</Label>
            <Input
              data-testid="input-api-key"
              type="password"
              placeholder={info?.has_key ? info.masked_key : "Paste your EnsembleData key"}
              value={key}
              onChange={(e) => setKey(e.target.value)}
              className="mt-1"
            />
            <p className="mt-2 flex items-center gap-1.5 text-xs text-neutral-500">
              {info?.has_key ? (
                <><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> A key is currently saved. Live refreshes are enabled.</>
              ) : (
                <><RefreshCw className="h-3.5 w-3.5" /> No key saved — refreshes use the built-in realistic simulator.</>
              )}
            </p>
          </div>

          <div className="mt-6 flex gap-2">
            <Button data-testid="save-api-key" onClick={save} disabled={saving} className="rounded-full bg-[#35606E] hover:bg-[#294b57]">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save key
            </Button>
            {info?.has_key && (
              <Button data-testid="clear-api-key" onClick={() => { setKey(""); api.put("/settings", { ensemble_api_key: "" }).then(() => { toast.success("Key cleared"); load(); }); }} variant="outline" className="rounded-full border-neutral-300">
                Clear
              </Button>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
}
